import {
  Banner,
  Flex,
  GreyGrey80,
  Primary13Regular,
  SimpleModal,
  Text,
  useNotifications
} from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { add, format } from '@sequencehq/utils/dates'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { phaseDateFormat } from 'modules/Cube/cube.constants'
import { getBillingToday } from 'modules/Cube/domain/postActionStages/queries/hasAmendmentDatePassed'
import {
  AmendmentRefusal,
  getAmendmentRefusal
} from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/utils/getAmendmentRefusal'
import { getExecuteConditions } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/utils/getExecuteConditions'
import { useAmendedSchedule } from 'modules/Cube/view/layouts/quote/QuoteEditorControls/AmendmentEffectiveFrom/useAmendedSchedule'
import { getPhasesAppliedFrom } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/utils/getPhasesAppliedFrom'
import { getScheduleBillingDayOn } from '@sequencehq/utils'
import { hasPhaseAlignmentWarnings } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/utils/hasPhaseAlignmentWarnings'
import { FC, useState } from 'react'
import { useNavigate } from 'react-router-dom'

type ExecuteAmendmentQuoteModalProps = {
  onClose: () => void
}

export const ExecuteAmendmentQuoteModal: FC<
  ExecuteAmendmentQuoteModalProps
> = ({ onClose }) => {
  const cubeContext = useCubeContext()
  const navigate = useNavigate()
  const { displayNotification } = useNotifications()
  const executeMutation = useMutation(dashboard20240730Client.postQuoteExecute)
  const amendedSchedule = useAmendedSchedule()

  const quoteId = cubeContext.queries.rawData.data.common.id
  const amendmentStartDate =
    cubeContext.queries.rawData.data.quote.amendment?.effectiveFrom
  // Read once when the modal opens, so what it says doesn't shift while open.
  const [ourToday] = useState(getBillingToday)
  // Billing can refuse for reasons we can put right here, and each refusal
  // tells the next attempt what to ask for.
  const [refusals, setRefusals] = useState<AmendmentRefusal[]>([])
  // An amendment can't change a schedule in the past, so one executed after
  // its date takes effect from today instead.
  const { today, isLate, isResetRequiredByBilling } = getExecuteConditions({
    effectiveFrom: amendmentStartDate,
    ourToday,
    refusals
  })
  const appliedFrom = isLate ? today : amendmentStartDate
  // The schedule's snapshot has no billing day, but the schedule itself does,
  // and it's locked while the amendment is live. If it fails to load, the
  // check falls back to the quote's billing day.
  const originalBillingDay =
    amendedSchedule.schedule &&
    getScheduleBillingDayOn({
      schedule: amendedSchedule.schedule,
      date: add(today, { days: -1 })
    })
  // An amendment quote holds the whole amended schedule, from the original
  // start date, so its own phases are what the warning is checked against -
  // moved to today when that's when the amendment takes effect. Our check can
  // miss a billing period billing would split, so when billing refuses for
  // that reason we warn and ask for the reset all the same.
  const hasAlignmentWarning =
    isResetRequiredByBilling ||
    hasPhaseAlignmentWarnings(
      isLate
        ? getPhasesAppliedFrom({
            orderedPhases: cubeContext.queries.orderedPhases,
            appliedFrom: today,
            reducerState: cubeContext.queries.rawData,
            originalBillingDay
          })
        : cubeContext.queries.orderedPhases
    )

  return (
    <SimpleModal
      isOpen
      data-testid="modal.executeAmendmentQuote"
      variant="dialog"
      title="Amend billing schedule"
      submitButtonText="Amend billing schedule"
      onClose={onClose}
      submitting={executeMutation.isPending}
      // A late amendment's warning needs the schedule's billing day, so it
      // waits for the schedule rather than guess.
      submitDisabled={isLate && amendedSchedule.isLoading}
      contentProps={{ minHeight: 'fit-content' }}
      width="414px"
      onSubmit={async () => {
        try {
          const executedQuote = await executeMutation.mutateAsync({
            id: quoteId,
            body: {
              scheduleAction: {
                mode: 'AMEND_EXISTING',
                ...(isLate && {
                  whenEffectiveDatePassed: 'TAKE_EFFECT_TODAY' as const
                }),
                // Only sent when the amendment would otherwise change prices
                // part way through a billing period - the reset moves the
                // billing day onto the amendment start date so it can't. It
                // also resets any later date that needs it, so it covers
                // whichever date billing refused for.
                ...(hasAlignmentWarning && {
                  phaseRecurrencePreference: 'RESET' as const
                })
              }
            }
          })

          await cubeContext.mutators.external.in.core()

          const billingScheduleId = executedQuote?.billingScheduleId
          displayNotification('Billing schedule amended', {
            type: 'success',
            truncate: false,
            'data-testid': 'executeAmendmentQuote.successToast',
            ...(billingScheduleId && {
              viewLabel: 'View schedule',
              onClickView: () =>
                navigate(`/billing-schedules/${billingScheduleId}`)
            })
          })

          return true
        } catch (error) {
          // Billing can refuse for reasons we can put right here: its clock
          // can be a day ahead of ours, or it can find a billing period our
          // check missed. Show the amendment the way billing sees it, and let
          // the user confirm it again.
          const refusal = getAmendmentRefusal(error)
          if (refusal) {
            setRefusals(previous => [...previous, refusal])
            return false
          }

          // The API's own message is written for API users, so we keep to our wording here.
          displayNotification('Failed to amend billing schedule', {
            type: 'error'
          })

          // A refusal usually means the quote moved on behind us - most often it was superseded by
          // another amendment of the same schedule. Reload so the editor says so.
          await cubeContext.mutators.external.in.core()

          return false
        }
      }}
    >
      <Flex flexDir="column" gap="12px">
        <Text {...Primary13Regular} color={GreyGrey80}>
          This will apply the amendments from this quote to the customer's
          active billing schedule.
        </Text>
        {isLate && amendmentStartDate && (
          <Banner
            variant="warning"
            data-testid="modal.executeAmendmentQuote.lateWarning"
          >
            This amendment was due to take effect on{' '}
            {format(amendmentStartDate, phaseDateFormat)}. It will take effect
            from today, {format(today, phaseDateFormat)}, instead.
          </Banner>
        )}
        {hasAlignmentWarning && appliedFrom && (
          <Banner
            variant="warning"
            data-testid="modal.executeAmendmentQuote.alignmentWarning"
          >
            We'll set the billing day of the amended schedule to the{' '}
            {format(appliedFrom, 'do')} from{' '}
            {format(appliedFrom, phaseDateFormat)}.
          </Banner>
        )}
      </Flex>
    </SimpleModal>
  )
}
